import React, { useState, useEffect, useCallback } from 'react';
import ModuleLayout from '../../layouts/ModuleLayout';
import {
  Clock,
  ChevronLeft,
  ChevronRight,
  Download,
  BarChart3,
  FileText,
  Settings,
  Edit2,
} from 'lucide-react';
import toast from 'react-hot-toast';

// Import modular components
import AttendanceDashboard from '../../components/Admin/HRM/Attendance/AttendanceDashboard';
import DailyAttendanceLog from '../../components/Admin/HRM/Attendance/DailyAttendanceLog';
import RegularizationRequests from '../../components/Admin/HRM/Attendance/RegularizationRequests';
import AttendanceReports from '../../components/Admin/HRM/Attendance/AttendanceReports';
import AttendanceSettings from '../../components/Admin/HRM/Attendance/AttendanceSettings';

// Import shared data and utilities
import {
  generateEmployees,
  generateAttendanceLog,
  generateWeeklyData,
  calculateTodayStats,
  generateRegularizationRequests,
  exportToCSV,
} from '../../components/Admin/HRM/Attendance/attendanceData';

const TABS = [
  { id: 'dashboard', label: 'Dashboard', icon: BarChart3 },
  { id: 'daily', label: 'Daily Log', icon: Clock },
  { id: 'regularize', label: 'Regularization', icon: Edit2 },
  { id: 'reports', label: 'Reports', icon: FileText },
  { id: 'settings', label: 'Settings', icon: Settings },
];

export default function AttendanceManagementPage() {
  
  const [activeTab, setActiveTab] = useState('dashboard');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  // Data state
  const [employees] = useState(() => generateEmployees());
  const [attendanceLog, setAttendanceLog] = useState([]);
  const [weeklyData] = useState(() => generateWeeklyData());
  const [regularizationRequests, setRegularizationRequests] = useState(() => generateRegularizationRequests());
  const [stats, setStats] = useState({});

  // Calculate pending regularization requests count
  const pendingCount = regularizationRequests.filter(r => r.status === 'pending').length;

  // Generate attendance log based on selected date
  useEffect(() => {
    const log = generateAttendanceLog(employees, selectedDate);
    setAttendanceLog(log);
    setStats(calculateTodayStats(log));
  }, [employees, selectedDate]);

  // Date navigation handlers
  const handlePrevDate = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() - 1);
    setSelectedDate(date.toISOString().split('T')[0]);
  };

  const handleNextDate = () => {
    const date = new Date(selectedDate);
    date.setDate(date.getDate() + 1);
    const today = new Date();
    if (date <= today) {
      setSelectedDate(date.toISOString().split('T')[0]);
    }
  };

  // Export handler
  const handleExport = useCallback(() => {
    const headers = ['Employee', 'Code', 'Department', 'Check In', 'Check Out', 'Hours', 'Status'];
    const data = attendanceLog.map(emp => [
      emp.name,
      emp.empCode,
      emp.department,
      emp.checkIn || '-',
      emp.checkOut || '-',
      emp.hours || '-',
      emp.status,
    ]);
    exportToCSV(`attendance_${selectedDate}.csv`, headers, data);
    toast.success('Attendance data exported successfully!');
  }, [attendanceLog, selectedDate]);

  // Dashboard handlers
  const handleViewAllActivity = () => {
    setActiveTab('daily');
  };

  const handleEmployeeClick = (employee) => {
    // Could open a detailed modal here
  };

  const handleDepartmentClick = (department) => {
    // Could filter daily log by department
  };

  const handleQuickAction = (actionId) => {
    switch (actionId) {
      case 'mark':
        setActiveTab('daily');
        break;
      case 'regularize':
        setActiveTab('regularize');
        break;
      case 'report':
        setActiveTab('reports');
        break;
      default:
        break;
    }
  };

  const handleBarClick = (dayData) => {
    // Bar click can navigate to that day's details
  };

  // Daily Log handlers
  const handleMarkAttendance = (attendanceData) => {
    // In real app, this would call API
    setAttendanceLog(prev => {
      const existingIndex = prev.findIndex(a => a.empCode === attendanceData.empCode);
      if (existingIndex >= 0) {
        const updated = [...prev];
        updated[existingIndex] = {
          ...updated[existingIndex],
          ...attendanceData,
        };
        return updated;
      }
      return [...prev, attendanceData];
    });
    setStats(calculateTodayStats(attendanceLog));
  };

  const handleEditAttendance = (updatedAttendance) => {
    setAttendanceLog(prev =>
      prev.map(a => (a.id === updatedAttendance.id ? { ...a, ...updatedAttendance } : a))
    );
    setStats(calculateTodayStats(attendanceLog));
  };

  // Regularization handlers
  const handleApproveRequest = (requestId) => {
    setRegularizationRequests(prev =>
      prev.map(r => (r.id === requestId ? { ...r, status: 'approved' } : r))
    );
  };

  const handleRejectRequest = (requestId) => {
    setRegularizationRequests(prev =>
      prev.map(r => (r.id === requestId ? { ...r, status: 'rejected' } : r))
    );
  };

  const handleCreateRequest = (newRequest) => {
    const request = {
      id: Date.now(),
      ...newRequest,
      submittedDate: new Date().toLocaleDateString('en-US', { day: '2-digit', month: 'short', year: 'numeric' }),
      status: 'pending',
    };
    setRegularizationRequests(prev => [request, ...prev]);
  };

  return (
    <ModuleLayout>
      <div className="min-h-full bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200">
          <div className="px-6 pt-5 pb-0">
            {/* Title Row */}
            <div className="flex items-center justify-between mb-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Attendance Management</h1>
                <p className="text-sm text-gray-500 mt-0.5">
                  Track employee attendance, overtime & regularizations
                </p>
              </div>
              <div className="flex items-center gap-3">
                <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                  <button
                    onClick={handlePrevDate}
                    className="p-1.5 hover:bg-white rounded transition"
                  >
                    <ChevronLeft size={16} className="text-gray-600" />
                  </button>
                  <input
                    type="date"
                    value={selectedDate}
                    onChange={(e) => setSelectedDate(e.target.value)}
                    max={new Date().toISOString().split('T')[0]}
                    className="px-2 py-1 bg-transparent text-sm font-medium text-gray-700 focus:outline-none"
                  />
                  <button
                    onClick={handleNextDate}
                    className="p-1.5 hover:bg-white rounded transition"
                  >
                    <ChevronRight size={16} className="text-gray-600" />
                  </button>
                </div>
                <button
                  onClick={handleExport}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition font-medium text-sm"
                >
                  <Download size={16} />
                  Export
                </button>
              </div>
            </div>

            {/* Tabs */}
            <div className="flex items-center gap-1 -mb-px">
              {TABS.map(tab => {
                const Icon = tab.icon;
                const isActive = activeTab === tab.id;
                return (
                  <button
                    key={tab.id}
                    onClick={() => setActiveTab(tab.id)}
                    className={`flex items-center gap-2 px-4 py-3 border-b-2 transition-all whitespace-nowrap ${
                      isActive
                        ? 'border-blue-600 text-blue-600 bg-white'
                        : 'border-transparent text-gray-500 hover:text-gray-700 hover:bg-gray-50'
                    }`}
                  >
                    <Icon size={16} />
                    <span className="font-medium text-sm">{tab.label}</span>
                    {tab.id === 'regularize' && pendingCount > 0 && (
                      <span className="bg-amber-100 text-amber-700 text-xs px-1.5 py-0.5 rounded-full font-medium">
                        {pendingCount}
                      </span>
                    )}
                  </button>
                );
              })}
            </div>
          </div>
        </div>

        {/* Content */}
        
        <div className="p-6">
          {activeTab === 'dashboard' && (
            <AttendanceDashboard
              stats={stats}
              weeklyData={weeklyData}
              attendanceLog={attendanceLog}
              onViewAllActivity={handleViewAllActivity}
              onEmployeeClick={handleEmployeeClick}
              onDepartmentClick={handleDepartmentClick}
              onQuickAction={handleQuickAction}
              onBarClick={handleBarClick}
            />
          )}

          {activeTab === 'daily' && (
            <DailyAttendanceLog
              attendanceLog={attendanceLog}
              employees={employees}
              selectedDate={selectedDate}
              onMarkAttendance={handleMarkAttendance}
              onEditAttendance={handleEditAttendance}
            />
          )}

          {activeTab === 'regularize' && (
            <RegularizationRequests
              requests={regularizationRequests}
              employees={employees}
              onApprove={handleApproveRequest}
              onReject={handleRejectRequest}
              onCreateRequest={handleCreateRequest}
            />
          )}

          {activeTab === 'reports' && (
            <AttendanceReports
              attendanceLog={attendanceLog}
              employees={employees}
              selectedDate={selectedDate}
            />
          )}

          {activeTab === 'settings' && (
            <AttendanceSettings />
          )}
        </div>
      </div>
    </ModuleLayout>
  );
}
